import { AccountProvider } from '@collinsonx/constants/enums';
import { useLazyQuery } from '@collinsonx/utils/apollo';
import {
  OutletIdType,
  PrimaryProductAccessType,
  Outlet,
  Product,
  Status,
} from '@collinsonx/utils/generatedTypes/graphql';
import { getProductValuation } from '@collinsonx/utils/queries';
import { getItem, JSONParse, setItem } from '@lib';
import {
  OriginType,
  sendErrorLog,
  sendInfoLog,
  useLogStorage,
} from '@lib/logging';
import {
  convertLocationToProduct,
  convertOutletToProduct,
  convertValuationToProduct,
} from 'adapters/productAdapter';
import { BookingError, BookingInfo } from 'config/datadog';
import {
  shouldUseValuationPricing,
  useFTWithOutlet,
  useValuationService,
} from 'config/features';
import { PRODUCT } from 'config/storage';
import { ProductContext } from 'context/ProductContext';
import { getBookingOutlet, getBookingProductByCode } from 'gql-queries';
import isEqual from 'lodash/isEqual';
import { useContext } from 'react';
import {
  MappedProductData,
  ProductValuationType,
} from 'types/mappedProductData';

import useError from './useError';
import useGetUserSession from './useGetUserSession';
import useLocale from './useLocale';
import usePayload from './usePayload';
import useProduct from './useProduct';

const useHandleProduct = () => {
  const { setProduct } = useContext(ProductContext);
  const { productID } = useProduct();
  const translation = useLocale();
  const { setError } = useError();
  const { userIsSignedIn } = useGetUserSession();
  const { setProductData } = useLogStorage();
  const { consumerID, isLocationProduct, loungeCode, membershipID, payload } =
    usePayload();

  const { accountProvider } = payload || {};
  const isPreferredProgramme = accountProvider === AccountProvider.PP_PREFERRED;
  const loungeAccessType = isPreferredProgramme
    ? PrimaryProductAccessType.Reservation
    : PrimaryProductAccessType.ReservationFeeOnly;

  const productDeps = [loungeCode, accountProvider, userIsSignedIn, consumerID];
  const productDepsLoaded = productDeps.every(Boolean);

  const handleProductError = () => {
    sendErrorLog(BookingError.LOUNGE_NOT_FOUND, {
      origin: OriginType.API,
    });
    setError({
      key: BookingError.LOUNGE_NOT_FOUND,
      message: translation.generic.error.loungeNoFound,
    });
  };

  const [fetchOutletRequest] = useLazyQuery<{
    getOutlet: Outlet;
  }>(getBookingOutlet, {
    onError: handleProductError,
    variables: {
      additionalFilters: {
        accessType: loungeAccessType,
        programme: accountProvider,
      },
      getOutletId: loungeCode,
      outletIdType: OutletIdType.Code,
    },
  });

  const [fetchFTWithOutletRequest] = useLazyQuery<{
    getOutlet: Outlet;
  }>(getBookingOutlet, {
    onError: handleProductError,
    variables: {
      additionalFilters: {
        accessType: PrimaryProductAccessType.Reservation,
        programme: accountProvider,
      },
      getOutletId: loungeCode,
      outletIdType: OutletIdType.Code,
    },
  });

  const [fetchLocationProductRequest] = useLazyQuery<{
    getProductByCode: Product;
  }>(getBookingProductByCode, {
    notifyOnNetworkStatusChange: true,
    onError: handleProductError,
    variables: {
      code: loungeCode,
      filters: {
        accessType: [
          PrimaryProductAccessType.ReservationFeeOnly,
          PrimaryProductAccessType.Reservation,
        ],
        status: Status.Active,
      },
    },
  });

  const [fetchProductValuation] = useLazyQuery<{
    getProductValuation: ProductValuationType;
  }>(getProductValuation, {
    variables: {
      valuationInput: {
        consumerID,
        membershipID,
        productID,
        visitors: [{ isGuest: false, visitorType: 'ADULT' }],
      },
    },
  });

  const fetchLocationProduct = async () => {
    const productRes = await fetchLocationProductRequest();

    const locationProduct = productRes.data?.getProductByCode;

    if (!locationProduct) {
      handleProductError();
      return null;
    }

    const productParsed = convertLocationToProduct(locationProduct);

    sendInfoLog(BookingInfo.LOCATION_PRODUCT_RESPONSE, {
      locationProductParsed: productParsed,
      locationProductResponse: locationProduct,
    });

    return convertLocationToProduct(locationProduct);
  };

  const fetchProduct = async () => {
    const productRes = await fetchOutletRequest();

    const product = productRes?.data?.getOutlet;

    const loungeProduct = product?.products?.filter(
      (outlet) => outlet?.accessType === loungeAccessType
    )[0];

    if (!product || !loungeProduct) {
      handleProductError();
      return null;
    }

    const productParsed = convertOutletToProduct(
      product,
      loungeProduct,
      false,
      isPreferredProgramme
    );

    sendInfoLog(BookingInfo.OUTLET_PRODUCT_RESPONSE, {
      outletProductParsed: productParsed,
      outletProductResponse: product,
    });

    return productParsed;
  };

  const fetchFTWithOutletProduct = async () => {
    const outletRes = await fetchFTWithOutletRequest();
    const outletData = outletRes?.data?.getOutlet;

    const ftWithOutletProduct = outletData?.products?.filter(
      (product) => product?.accessType === PrimaryProductAccessType.Reservation
    )[0];

    if (!outletData || !ftWithOutletProduct) {
      return null;
    }

    const productParsed = convertOutletToProduct(
      outletData,
      ftWithOutletProduct,
      isLocationProduct,
      true
    );

    sendInfoLog(BookingInfo.OUTLET_PRODUCT_RESPONSE, {
      outletProductParsed: productParsed,
      outletProductResponse: outletData,
    });

    return productParsed;
  };

  const fetchValuation = async () => {
    const productRes = await fetchProductValuation();

    const product = productRes?.data?.getProductValuation;

    if (!product) return {};

    const productParsed = convertValuationToProduct(product);

    sendInfoLog(BookingInfo.VALUATION_PRODUCT_RESPONSE, {
      valuationProductParsed: productParsed,
      valuationProductResponse: product,
    });

    return productParsed;
  };

  const retrieveProduct = async (): Promise<MappedProductData | null> => {
    const productCached = JSONParse(getItem(PRODUCT), {});
    const { data, deps } = productCached;

    const shouldRehydrate = !isEqual(deps, productDeps) || !data;
    if (!shouldRehydrate) return data;

    const productRes = isLocationProduct
      ? useFTWithOutlet
        ? await getFastTrackProductFallback()
        : await fetchLocationProduct()
      : await fetchProduct();

    if (!productRes) {
      handleProductError();
      return null;
    }

    sendInfoLog(BookingInfo.LOUNGE_REQUEST, {
      loungeCode: productRes.loungeCode,
    });

    setItem(PRODUCT, JSON.stringify({ data: productRes, deps: productDeps }));

    return productRes;
  };

  const getFastTrackProductFallback = async () => {
    let productParsed: MappedProductData | null = null;
    productParsed = await fetchFTWithOutletProduct();

    if (!productParsed) {
      sendInfoLog(BookingInfo.USE_FT_FALLBACK, {
        message:
          'Failed to fetch product with FT with outlet, trying to fetch location product',
      });
      productParsed = await fetchLocationProduct();
    }

    if (!productParsed) {
      handleProductError();
      return null;
    }

    sendInfoLog(BookingInfo.PRODUCT_RESPONSE, {
      productParsed,
    });

    return productParsed;
  };

  const hydrateProductState = async (): Promise<void> => {
    const product = await retrieveProduct();

    if (!product) return;

    // Both `useValuationService` and the category-appropriate pricing
    // flag must be on for the shared `convertValuationToProduct` mapping
    // to reach `product.saleprice`. This prevents the new
    // `memberPrice`-aware mapping from leaking into any environment where
    // `NEXT_PUBLIC_VALUATION_SERVICE=ON` but valuation-driven pricing is
    // not yet enabled for this product's category, keeping the legacy
    // `usePrice` totals stable.
    if (
      !useValuationService ||
      !shouldUseValuationPricing(product.category) ||
      !product.productID ||
      !membershipID
    ) {
      setProduct(product);
      return;
    }

    const valuation = await fetchValuation();

    const productState = {
      ...product,
      ...valuation,
    };

    setProduct(productState);
    setProductData({
      productID: productState.productID ?? null,
    });
  };

  return {
    hydrateProductState,
    productDeps,
    productDepsLoaded,
  };
};

export default useHandleProduct;
